import { EConfigurationNames } from '@ConfigLoader/ConfigFiles';
import ConfigLoader from '@ConfigLoader/ConfigLoader';
import { IAsset } from '@ConfigLoader/Configs';
import { GameStore } from '@GameStore/GameStore';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import {
    IAuthenticateGameConfig,
    IBullshitCollectorConfig,
    IDynamicCasinoJackpotConfig,
    IDynamicCasinoJackpotFeature,
    IFeatures,
    IFreeGameFeature,
    IJackpotSymbol,
    IJackpotWin,
    IMaxWinFeature,
    IMaxWinSymbol,
    IMultiSymbolFeature,
    ISoundsConfiguration,
    ISpecialGamesFeature,
    IStaticJackpotFeature,
    IWildWheelConfiguration,
    wildRoles
} from '@GFTypes/GameStoreTypes';
import { IMultiSymbolConfig } from '@GFTypes/GameStoreTypes/MultiSymbolConfig';
import { IPayTableSymbol } from '@GFTypes/Responses/AuthenticateResponse';
import { ESpecialGamesType } from '@GFTypes/SpecialGames';
import { BonusBuy } from '@Managers/BonusBuy/BonusBuy';
import BackgroundSoundManager from '@Managers/SoundManager/BackgroundSoundManager';
import { Assets } from 'pixi.js';
import { AboveReels } from './AboveReels/AboveReels';
import { createDoubleCoinMap } from './API/Data/DoubleLockAndRollCoinDataProcessor';
import { OfAKindModifier } from './Events/EventModifiers/OfAKindModifier';
import { GambleCard } from './Gamble/Elements/GambleCard';
import { GambleResult } from './Gamble/Elements/GambleResult';
import { Gamble } from './Gamble/Gamble';
import { InfoDisplay } from './InfoDisplay/InfoDisplay';
import { ESymbolRoles } from './Reels/SymbolRoles';
import { Scene } from './Scene/Scene';
import { SpecialGamesSpine } from './SpecialGames/SpecialGamesSpine';
import { Tension } from './Tension/Tension';
import { UnderWin } from './UnderWin/UnderWin';
import { getAllEventModifiers } from './Utils/ModifierUtils';
import { IWinMultiplierPresentationConfig } from './Win/WinMultiplierPresentation';

export class FeatureDetector {
    public static getFeatures(scene: Scene, gameStore: GameStore, symbols: IPayTableSymbol[][], declaredAssets: IAsset[]): IFeatures {
        const authGameConfig = gameStore.get(EGameStoreKeys.AuthenticateGameConfig);
        const wildWheelConfig = gameStore.get(EGameStoreKeys.WildWheelConfiguration);
        const assetsByKey = this.indexAssetsByKey(declaredAssets);
        const symbolsByRole = this.indexSymbolsByRole(symbols);
        const symbolIdsByRole = this.indexSymbolIdsByRole(symbolsByRole);
        const soundsConfiguration = gameStore.get(EGameStoreKeys.SoundsConfiguration);
        const {
            jackpotWins: jackpotWinConfig,
            collectors: collectorConfig,
            dynamicCasinoJackpot: dynamicCasinoJackpotConfig,
            win: { winMultiplierPresentation: winMultiplierPresentationConfig },
            multiSymbol: multiSymbolConfig
        } = ConfigLoader.getConfiguration(EConfigurationNames.GAME);
        const wild = this.getWilds(symbolIdsByRole);
        const expandingWild = this.getExpandingWilds(symbolIdsByRole);
        const scatter = this.getScatters(symbolIdsByRole) || this.getBonusWheelScatters(symbolIdsByRole);
        const wildWheel = this.getWildWheel(symbolIdsByRole, assetsByKey, wildWheelConfig);
        const aboveReels = this.getAboveReels(assetsByKey);
        const tension = this.getTension(assetsByKey);
        const gamble = this.getGamble(authGameConfig, assetsByKey);
        const underWin = this.getUnderWin(assetsByKey);
        const infoDisplay = this.getInfoDisplay(assetsByKey);
        const specialGames = this.getSpecialGames(symbolIdsByRole, symbolsByRole, assetsByKey);
        const backgroundMusic = this.getBackgroundMusic(soundsConfiguration);
        const maxWin = this.getMaxWin(authGameConfig);
        const interchangeableSymbol = this.getInterchangeableSymbol(symbolIdsByRole);
        const expandingSymbol = this.getExpandingSymbols(symbolIdsByRole);
        const ofAKind = this.getOfAKind(scene, declaredAssets);
        const staticJackpot = this.getStaticJackpot(authGameConfig, jackpotWinConfig);
        const winlines = this.getWinlines(authGameConfig);
        const bullshitCollector = this.getBullshitCollector(collectorConfig);
        const collector = this.getCollector(authGameConfig);
        const bonusBuy = this.getBonusBuy(assetsByKey);
        const dynamicCasinoJackpot = this.getDynamicCasinoJackpot(dynamicCasinoJackpotConfig);
        const winMultiplierPresentation = this.getWinMultiplierPresentation(winMultiplierPresentationConfig);
        const multiSymbol = this.getMultiSymbol(symbolsByRole, multiSymbolConfig);

        const features: IFeatures = {
            ...(wild && { wild }),
            ...(expandingWild && { expandingWild }),
            ...(scatter && { scatter }),
            ...(wildWheel && { wildWheel }),
            ...(aboveReels && { aboveReels }),
            ...(tension && { tension }),
            ...(gamble && { gamble }),
            ...(underWin && { underWin }),
            ...(infoDisplay && { infoDisplay }),
            ...(specialGames && { specialGames }),
            ...(backgroundMusic && { backgroundMusic }),
            ...(maxWin && { maxWin }),
            ...(interchangeableSymbol && { interchangeableSymbol }),
            ...(expandingSymbol && { expandingSymbol }),
            ...(ofAKind && { ofAKind }),
            ...(staticJackpot && { staticJackpot }),
            ...(winlines && { winlines }),
            ...(bullshitCollector && { bullshitCollector }),
            ...(collector && { collector }),
            ...(dynamicCasinoJackpot && { dynamicCasinoJackpot }),
            ...(bonusBuy && { bonusBuy }),
            ...(winMultiplierPresentation && { winMultiplierPresentation }),
            ...(multiSymbol && { multiSymbol })
        };

        return features;
    }

    private static getDynamicCasinoJackpot(config: IDynamicCasinoJackpotConfig): IDynamicCasinoJackpotFeature {
        return { available: config.available };
    }

    public static getWinlines(authGameConfig: IAuthenticateGameConfig): boolean | undefined {
        const { powerLines, payLines } = authGameConfig;
        const hasWinlines = !powerLines && payLines.length > 0;
        return hasWinlines;
    }

    public static getOfAKind(scene: Scene, declaredAssets: IAsset[]): number[] | undefined {
        const ofAKindModifiers: Set<number> = new Set();

        declaredAssets.forEach(({ key }) => {
            const spineData = Assets.get(key);
            const animations = spineData?.animations;

            if (!animations || typeof animations !== 'object') return;

            Object.keys(animations).forEach((animName) => {
                const ofAKindModifier = OfAKindModifier.getOfAKindModifier(getAllEventModifiers(animName));
                if (ofAKindModifier) ofAKindModifiers.add(ofAKindModifier);
            });
        });
        return this.collapseIfEmpty([...ofAKindModifiers]);
    }

    private static indexSymbolsByRole(symbols: IPayTableSymbol[][]): Map<ESymbolRoles, IPayTableSymbol[]> {
        const symbolsByRole = new Map<ESymbolRoles, IPayTableSymbol[]>();
        for (const symbol of symbols[0]) {
            symbol.roles?.forEach((role) => {
                role.types.forEach((type) => {
                    const existing = symbolsByRole.get(type);
                    if (existing) existing.push(symbol);
                    else symbolsByRole.set(type, [symbol]);
                });
            });
        }
        return symbolsByRole;
    }

    private static indexSymbolIdsByRole(symbolsByRole: Map<ESymbolRoles, IPayTableSymbol[]>): Map<ESymbolRoles, number[]> {
        const result = new Map<ESymbolRoles, number[]>();
        symbolsByRole.forEach((symbols, role) => {
            result.set(
                role,
                symbols.map((symbol) => symbol.symbolId)
            );
        });
        return result;
    }

    private static indexAssetsByKey(declaredAssets: IAsset[]): Map<string, IAsset> {
        return new Map(declaredAssets.map((asset) => [asset.key, asset]));
    }

    private static getWilds(symbolIdsByRole: Map<ESymbolRoles, number[]>): number[] | undefined {
        const wilds = [...wildRoles]
            .map((role) => symbolIdsByRole.get(role))
            .filter((ids) => !!ids)
            .flat();
        return this.collapseIfEmpty(wilds);
    }

    private static getExpandingWilds(symbolIdsByRole: Map<ESymbolRoles, number[]>): Map<ESymbolRoles, number[]> | undefined {
        const expandingWildFeature: Map<ESymbolRoles, number[]> = new Map();

        for (const [symbolRole, symbolIds] of symbolIdsByRole) {
            if (
                symbolRole === ESymbolRoles.EXPANDING_WILD_VERTICAL ||
                symbolRole === ESymbolRoles.EXPANDING_WILD_HORIZONTAL ||
                symbolRole === ESymbolRoles.EXPANDING_WILD_360
            ) {
                expandingWildFeature.set(symbolRole, symbolIds);
            }
        }

        return this.collapseIfEmpty(expandingWildFeature);
    }

    private static getScatters(symbolIdsByRole: Map<ESymbolRoles, number[]>): number[] | undefined {
        return symbolIdsByRole.get(ESymbolRoles.SCATTER);
    }

    private static getBonusWheelScatters(symbolIdsByRole: Map<ESymbolRoles, number[]>): number[] | undefined {
        return symbolIdsByRole.get(ESymbolRoles.BONUS_WHEEL_SCATTER);
    }

    private static getWildWheel(
        symbolIdsByRole: Map<ESymbolRoles, number[]>,
        assetsByKey: Map<string, IAsset>,
        wildWheelConfig: IWildWheelConfiguration
    ): boolean | undefined {
        const hasProperSpines = wildWheelConfig?.wheels?.every((wheel) => assetsByKey.has(wheel.spineName));
        return hasProperSpines ?? undefined;
    }

    private static getAboveReels(assetsByKey: Map<string, IAsset>): boolean {
        return assetsByKey.has(AboveReels.SPINE_KEY);
    }

    private static getTension(assetsByKey: Map<string, IAsset>): boolean {
        return assetsByKey.has(Tension.SPINE_KEY);
    }

    private static getGamble(authGameConfig: IAuthenticateGameConfig, assetsByKey: Map<string, IAsset>): boolean {
        return (
            authGameConfig.gamble.maxTries > 0 &&
            assetsByKey.has(Gamble.BACKGROUND_SPINE_KEY) &&
            assetsByKey.has(GambleCard.CARD_SPINE_KEY) &&
            assetsByKey.has(GambleResult.RESULT_SPINE_KEY)
        );
    }

    private static getUnderWin(assetsByKey: Map<string, IAsset>): boolean {
        return assetsByKey.has(UnderWin.SPINE_KEY);
    }

    private static getBonusBuy(assetsByKey: Map<string, IAsset>): boolean {
        return assetsByKey.has(BonusBuy.SPINE_KEY);
    }

    private static getInfoDisplay(assetsByKey: Map<string, IAsset>): boolean {
        return assetsByKey.has(InfoDisplay.SPINE_KEY);
    }

    private static getSpecialGames(
        symbolIdsByRole: Map<ESymbolRoles, number[]>,
        symbolsByRole: Map<ESymbolRoles, IPayTableSymbol[]>,
        assetsByKey: Map<string, IAsset>
    ): ISpecialGamesFeature | undefined {
        if (!assetsByKey.get(SpecialGamesSpine.SPINE_KEY)) return undefined;
        const lockAndRoll = this.getLockAndRoll(symbolIdsByRole);
        const freeGames = this.getFreeGames(symbolIdsByRole, symbolsByRole);

        //TODO: FG add check for spines?
        return this.collapseIfEmpty({
            [ESpecialGamesType.LOCK_AND_ROLL]: lockAndRoll,
            [ESpecialGamesType.FREE_GAMES]: freeGames
        });
    }

    private static getLockAndRoll(symbolIdsByRole: Map<ESymbolRoles, number[]>): Map<ESymbolRoles, number[]> | undefined {
        const lockAndRollFeature: Map<ESymbolRoles, number[]> = new Map();
        const coinIds = symbolIdsByRole.get(ESymbolRoles.LOCK_AND_ROLL_COIN);
        const bonusCoinIds = symbolIdsByRole.get(ESymbolRoles.LOCK_AND_ROLL_BONUS_COIN);
        if (coinIds) lockAndRollFeature.set(ESymbolRoles.LOCK_AND_ROLL_COIN, coinIds);
        if (bonusCoinIds) lockAndRollFeature.set(ESymbolRoles.LOCK_AND_ROLL_BONUS_COIN, bonusCoinIds);
        return this.collapseIfEmpty(lockAndRollFeature);
    }

    private static getFreeGames(
        symbolIdsByRole: Map<ESymbolRoles, number[]>,
        symbolsByRole: Map<ESymbolRoles, IPayTableSymbol[]>
    ): IFreeGameFeature | undefined {
        const scatterIds = symbolIdsByRole.get(ESymbolRoles.SCATTER);
        const specialSymbolIdsByGameType = this.getSpecialSymbolIdsByGameType(symbolsByRole);
        const freeGamesFeature: IFreeGameFeature = {} as IFreeGameFeature;
        if (scatterIds) freeGamesFeature[ESymbolRoles.SCATTER] = scatterIds;
        if (specialSymbolIdsByGameType.size > 0) freeGamesFeature[ESymbolRoles.SPECIAL_SYMBOL] = specialSymbolIdsByGameType;
        return this.collapseIfEmpty(freeGamesFeature);
    }

    private static getSpecialSymbolIdsByGameType(symbolsByRole: Map<ESymbolRoles, IPayTableSymbol[]>): Map<number, number> {
        const result = new Map<number, number>();
        const specialSymbols = symbolsByRole.get(ESymbolRoles.SPECIAL_SYMBOL);
        if (!specialSymbols) return result;
        specialSymbols.forEach((symbol) => {
            symbol.roles?.forEach((role) => {
                if (!role.types.includes(ESymbolRoles.SPECIAL_SYMBOL)) return;
                role.gameTypes?.forEach((gameType) => {
                    result.set(gameType, symbol.symbolId);
                });
            });
        });
        return result;
    }

    private static getBackgroundMusic(soundsConfiguration: ISoundsConfiguration): boolean {
        return !!soundsConfiguration.sounds[BackgroundSoundManager.BACKGROUND_SOUND_KEY];
    }

    private static getMaxWin(authGameConfig: IAuthenticateGameConfig): IMaxWinFeature | undefined {
        const symbols: IMaxWinSymbol[] | undefined = this.collapseIfEmpty(
            authGameConfig.symbols
                .flat()
                .filter((symbol) => symbol?.paytable?.[0]?.full)
                .map((symbol) => ({
                    id: symbol.symbolId,
                    full: symbol.paytable[0].full!
                }))
        );

        const maxWin = authGameConfig.maxWin;
        const staticJackpot = {
            symbols,
            maxWin
        };

        return this.collapseIfEmpty(staticJackpot as IMaxWinFeature);
    }

    private static getInterchangeableSymbol(symbolIdsByRole: Map<ESymbolRoles, number[]>): number[] | undefined {
        return symbolIdsByRole.get(ESymbolRoles.INTERCHANGEABLE_SYMBOL);
    }

    private static getExpandingSymbols(symbolIdsByRole: Map<ESymbolRoles, number[]>): Map<ESymbolRoles, number[]> | undefined {
        const expandingSymbols = new Map<ESymbolRoles, number[]>();

        const expandingRoles = [
            ESymbolRoles.EXPANDING_WILD_VERTICAL,
            ESymbolRoles.EXPANDING_WILD_HORIZONTAL,
            ESymbolRoles.EXPANDING_WILD_360,
            ESymbolRoles.SPECIAL_SYMBOL
        ];

        expandingRoles.forEach((role) => {
            const symbolIds = symbolIdsByRole.get(role);

            if (symbolIds?.length) {
                expandingSymbols.set(role, symbolIds);
            }
        });

        return this.collapseIfEmpty(expandingSymbols);
    }

    private static getStaticJackpot(authGameConfig: IAuthenticateGameConfig, jackpotWinConfig: IJackpotWin[]): IStaticJackpotFeature | undefined {
        const symbolsById = new Map<number, IJackpotSymbol>();

        authGameConfig.symbols[0]
            .filter((symbol) => symbol.roles?.some((role) => role.jackpotLevel !== undefined))
            .forEach((symbol) => {
                const id = symbol.symbolId;
                const jackpotId = symbol.roles?.find((role) => role.jackpotLevel !== undefined)?.jackpotLevel;
                const jackpotName = jackpotWinConfig.find((config) => config.jackpotId === jackpotId)?.name;

                if (jackpotId === undefined || jackpotName === undefined) return;

                symbolsById.set(id, {
                    id,
                    jackpotId,
                    jackpotName
                });
            });

        if (symbolsById.size === 0) return undefined;

        return { symbolsById };
    }

    private static getBullshitCollector(collectorConfig: IBullshitCollectorConfig[] | undefined): boolean {
        return !!collectorConfig;
    }

    private static getCollector(authGameConfig: IAuthenticateGameConfig): boolean {
        return !!authGameConfig.collectors;
    }

    private static getWinMultiplierPresentation(
        winMultiplierPresentationConfig: IWinMultiplierPresentationConfig
    ): IWinMultiplierPresentationConfig | undefined {
        return winMultiplierPresentationConfig.active ? winMultiplierPresentationConfig : undefined;
    }

    private static getMultiSymbol(symbolsByRole: Map<ESymbolRoles, IPayTableSymbol[]>, multiSymbolConfig: IMultiSymbolConfig): IMultiSymbolFeature | undefined {
        if (!multiSymbolConfig.enabled) return undefined;
        const componentSymbols = symbolsByRole.get(ESymbolRoles.LOCK_AND_ROLL_DOUBLE_COIN);
        if (!componentSymbols?.length) return undefined;

        const realComponentSymbols = componentSymbols.filter((symbol) => multiSymbolConfig.symbolAttachmentNamesBySymbolId?.[symbol.symbolId] !== undefined);

        if (!realComponentSymbols.length) return undefined;

        const sortedRealComponentSymbols = [...realComponentSymbols].sort((a, b) => a.symbolId - b.symbolId);

        const hasBlank = !!multiSymbolConfig.blankName;
        const realComponentSymbolIds = sortedRealComponentSymbols.map((symbol) => symbol.symbolId);
        const availableComponentSymbolIds: number[] = hasBlank ? [0, ...realComponentSymbolIds] : realComponentSymbolIds;

        const base = availableComponentSymbolIds.length;
        const componentSymbolCount = 2;

        const firstComponentSymbolId = realComponentSymbolIds[0];
        const nextSymbolId = firstComponentSymbolId + 1;

        const componentSymbolIdsByMultiSymbolIds = new Map<number, number[]>();
        const positionalCombinations = this.generatePositionalCombinations(componentSymbolCount, base);

        for (const positions of positionalCombinations) {
            const encoded = this.combineMultiBonusSymbols(positions, base);
            componentSymbolIdsByMultiSymbolIds.set(
                nextSymbolId + encoded - 1,
                positions.map((position) => availableComponentSymbolIds[position - 1])
            );
        }

        const collapsedIds = this.collapseIfEmpty(componentSymbolIdsByMultiSymbolIds);

        if (!collapsedIds) return undefined;

        // TODO: refactor this so this map is created in one place
        const temp = createDoubleCoinMap(firstComponentSymbolId);

        return {
            config: multiSymbolConfig,
            componentSymbolIdsByMultiSymbolIds: temp
        };
    }

    private static combineMultiBonusSymbols(symbols: number[], base: number): number {
        const len = symbols.length;
        let result = 0;

        for (let index = 0; index < len; index++) {
            result += (symbols[index] - 1) * Math.pow(base, len - index - 1);
        }

        return result + 1;
    }

    private static generatePositionalCombinations(count: number, base: number): number[][] {
        if (count === 0) return [[]];

        const tails = this.generatePositionalCombinations(count - 1, base);
        const combinations: number[][] = [];

        for (let symbol = 1; symbol <= base; symbol++) {
            for (const tail of tails) {
                combinations.push([symbol, ...tail]);
            }
        }

        return combinations;
    }

    private static collapseIfEmpty<T>(obj: T[] | undefined): T[] | undefined;
    private static collapseIfEmpty<K, V>(obj: Map<K, V> | undefined): Map<K, V> | undefined;
    private static collapseIfEmpty<T extends object>(obj: T | undefined): T | undefined;
    private static collapseIfEmpty(obj: any): any {
        if (obj instanceof Map) return obj.size > 0 ? obj : undefined;
        if (Array.isArray(obj)) return obj.length > 0 ? obj : undefined;
        if (obj && typeof obj === 'object') return Object.values(obj).some((v) => v !== undefined) ? obj : undefined;
        return undefined;
    }
}
